import { Component, EventEmitter, HostListener, Input, Output, ViewEncapsulation } from '@angular/core';

/** Leading glyph on an option card. A closed set on purpose — the point of a shared popup is that
 *  the same choice reads the same everywhere, so a page picks from this list rather than passing
 *  raw SVG in. Add a case here (and in the template's @switch) when a new choice needs one. */
export type PmChoiceIcon = 'building' | 'pencil' | 'upload' | 'user';

/** One card in the body. `id` is what comes back on (choose). */
export interface PmChoiceOption {
  id: string;
  title: string;
  description: string;
  icon: PmChoiceIcon;
}

/**
 * "Pick one of these routes" modal — the Add New Client popup is the reference design.
 *
 * Deliberately does NOT own the decision of whether it is open: the caller wraps it in an @if, the
 * same way the 210 hand-rolled popups in this app already do. That keeps it a pure renderer and
 * means no page has to sync an [open] input with its own signal.
 *
 * The backdrop is a real <button> rather than a div carrying a (click). Every existing popup in the
 * codebase uses the div form, which is why 164 of them carry a NOSONAR comment for it — a button
 * needs no suppression, gets keyboard dismissal for free, and is announced properly.
 */
@Component({
  selector: 'pm-choice-modal',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './pm-choice-modal.component.html',
  styleUrl: './pm-choice-modal.component.css'
})
export class PmChoiceModalComponent {
  @Input() title = '';
  @Input() subtitle = '';
  /** Glyph in the tinted tile beside the title. */
  @Input() icon: PmChoiceIcon = 'user';
  @Input() options: PmChoiceOption[] = [];

  /** An option card was picked; the payload is that option's `id`. */
  @Output() choose = new EventEmitter<string>();
  /** The X, the backdrop or Escape was used. */
  @Output() closed = new EventEmitter<void>();

  /** Ties the panel to its heading for screen readers without the caller having to invent an id. */
  readonly titleId = `pm-choice-modal-title-${Math.random().toString(36).slice(2, 9)}`;

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closed.emit();
  }
}
